import './global.d';
import { SessionManager } from './session-manager';
import { DEFAULT_IDLE_TIMEOUT } from './constants';
import { describeThrown, reportError } from './error-report';
import type { SessionManagerConfig } from './types';

function readConfigFromScript(): Partial<SessionManagerConfig> {
  const script = document.currentScript as HTMLScriptElement | null;
  if (!script) return {};

  const config: Partial<SessionManagerConfig> = {};

  const timeout = script.getAttribute('data-ps-idle-timeout');
  if (timeout) config.idleTimeout = parseInt(timeout, 10) || DEFAULT_IDLE_TIMEOUT;

  const selector = script.getAttribute('data-ps-outlink-selector');
  if (selector) config.outlinkSelector = selector;

  const param = script.getAttribute('data-ps-session-param');
  if (param) config.sessionParam = param;

  const domain = script.getAttribute('data-ps-cookie-domain');
  if (domain) config.cookieDomain = domain;

  const geoEndpoint = script.getAttribute('data-geo-endpoint');
  if (geoEndpoint) config.geoEndpoint = geoEndpoint;

  const contextTimeout = script.getAttribute('data-context-timeout');
  if (contextTimeout) config.contextTimeout = parseInt(contextTimeout, 10) || undefined;

  return config;
}

// Merge config sources: data attributes < window.PSSessionConfig
const scriptConfig = readConfigFromScript();
const windowConfig = window.PSSessionConfig ?? {};
const mergedConfig: Partial<SessionManagerConfig> = { ...scriptConfig, ...windowConfig };

/**
 * Build the manager and publish it on `window.PS.session`. Never throws.
 *
 * This runs at bundle eval, so an exception here takes the shared context down
 * for every consumer on the page at once — event-listener-client reads
 * `context.visitorId` from it, render-engine reads `context.id`, and both
 * degrade quietly when the global is simply missing, which is what made the
 * failure so hard to see. A component that publishes a global other scripts
 * depend on should not be able to fail by not existing.
 *
 * `storage.ts` now absorbs the realistic cause (a cookie jar that raises), so
 * this is a backstop for the rest of what the constructor touches: the UA
 * parse, `localStorage`, `crypto`, and the legacy `window.MF` globals.
 *
 * On failure the global is deliberately left unset rather than filled with a
 * half-built object. Consumers already read an absent `window.PS` as
 * "session-manager is not on this page", which is honest; a partly-populated
 * context would have them report values that were never resolved. The report is
 * what turns this from silence into something measurable.
 */
function createSessionManager(): SessionManager | null {
  try {
    const manager = new SessionManager(mergedConfig);
    window.PS = window.PS ?? {};
    window.PS.session = manager;
    return manager;
  } catch (thrown) {
    reportError({
      kind: 'session-manager: construction failed',
      tags: { component: 'session-manager' },
      // Name and message only. Nothing the constructor was resolving — ids,
      // geo, IP — has any business in an error tracker.
      extra: describeThrown(thrown),
    });
    return null;
  }
}

const sessionManager = createSessionManager();

// Decorate links once DOM is ready. No manager means no links to decorate.
function initLinks(): void {
  sessionManager?.initLinks();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLinks);
} else {
  initLinks();
}

export { sessionManager, SessionManager };
export { isVerbose, vlog } from './verbose';
export type { SessionManagerConfig, PSSessionManager, SessionContext, SessionGeo, SessionBrowser, SessionOs, SessionDevice, DecisionSessionPayload } from './types';
